import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

// import { catalogApi, studentApi } from '@/api';
import { studentApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  Input,
  LinkButton,
  LoadingState,
  SegmentedTabs,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { AccountSettings } from '@/features/student/AccountSettings';
import { PortfolioProjectsCard } from '@/features/student/PortfolioProjectsCard';
import { PortfolioLinkBar } from '@/features/student/PortfolioLinkBar';
import { PortfolioShareCard } from '@/features/student/PortfolioShareCard';
// import { ProfileCompletionCard } from '@/features/student/ProfileCompletionCard';
import { ProfileCvCard } from '@/features/student/ProfileCvCard';
import { ProfilePhotoField } from '@/features/student/ProfilePhotoField';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { StudentEducationPage } from '@/pages/student/EducationPage';
import { StudentExperiencePage } from '@/pages/student/ExperiencePage';
import { StudentPlanPage } from '@/pages/student/PlanPage';
import { StudentSkillsPage } from '@/pages/student/SkillsPage';
import { ProfileView } from '@/features/student/ProfileView';
import { ProfileVideoField } from '@/features/student/ProfileVideoField';

import {
  BIO_LIMITS,
  HEADLINE_LIMITS,
  latestBirthYear,
  earliestBirthDate,
  emailSchema,
  graduationYears,
  latestAvailableFrom,
  latestBirthDate,
  studentProfileSchema,
} from '@/validations';

/**
 * One page for everything about the student: what companies see, then how
 * they sign in. The account cards sit below the profile form, under
 * `#account`, which is where /student/account and the referral notification
 * now land.
 */
/** The profile's tabs; each but the first has its own address, `#education` and so on. */
const PROFILE_TABS = [
  { key: 'profile', label: 'Profile', icon: 'user' },
  { key: 'education', label: 'Education', icon: 'graduationCap' },
  { key: 'experience', label: 'Experience', icon: 'briefcase' },
  { key: 'skills', label: 'Skills', icon: 'zap' },
  // { key: 'video', label: 'Video', icon: 'video' },
  { key: 'video', label: 'Upload video', icon: 'video' },
  { key: 'account', label: 'Account', icon: 'shieldCheck' },
  { key: 'billing', label: 'Plan & billing', icon: 'creditCard' },
];

/** The tab an address points at, or null when it names none. */
function tabFromHash(hash) {
  const key = hash.replace('#', '');
  return PROFILE_TABS.some((tab) => tab.key === key) ? key : null;
}

// export function StudentProfilePage() {
//   return (
//     <div>
//       <PageHeader
//         title="Your profile"
//         description="What companies see when they find you, and how you sign in."
//       />
//       <PortfolioLinkBar className="mb-6" />
//       <ProfileDetails />
//     </div>
//   );
// }

/**
 * The profile opens as it reads to a company; "Edit profile" opens the tabs,
 * and saving the main details comes back to it. A link to a tab
 * (`#education`, `#account` from the account menu) opens the editor there.
 */
export function StudentProfilePage() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(() => tabFromHash(hash) !== null);

  useEffect(() => {
    if (tabFromHash(hash)) setEditing(true);
  }, [hash]);

  function showProfile() {
    setEditing(false);
    navigate({ hash: '' }, { replace: true });
  }

  return (
    <div>
      <PageHeader
        title="Your profile"
        description={
          editing
            ? 'What companies see when they find you, and how you sign in.'
            : 'How your profile reads to a company.'
        }
        action={
          editing ? (
            <Button variant="secondary" onClick={showProfile}>
              <Icon name="eye" size={16} />
              View profile
            </Button>
          ) : null
        }
      />
      <PortfolioLinkBar className="mb-6" />
      {editing ? (
        <ProfileDetails onSaved={showProfile} />
      ) : (
        <ProfileView
          actions={
            <Button size="sm" onClick={() => setEditing(true)}>
              <Icon name="pencil" size={15} />
              Edit profile
            </Button>
          }
        />
      )}
    </div>
  );
}

function ProfileDetails({ onSaved }) {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => tabFromHash(hash) ?? 'profile');
  const queryClient = useQueryClient();
  const toast = useToast();

  const {
    data: profile,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['student-profile'],
    queryFn: studentApi.getProfile,
  });

  // Only the Studies card used this, and it is off for now.
  // const { data: universities } = useQuery({
  //   queryKey: ['universities'],
  //   queryFn: () => catalogApi.listUniversities(),
  // });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting, isDirty },
    // Checked as soon as a field is left, then on every keystroke, so a
    // mistake shows before Save rather than after it.
  } = useForm({ resolver: zodResolver(studentProfileSchema), mode: 'onTouched' });
  const headline = watch('headline') ?? '';
  const bio = watch('bio') ?? '';

  // Populate the form once the profile arrives.
  useEffect(() => {
    if (!profile) return;
    reset({
      headline: profile.headline ?? '',
      bio: profile.bio ?? '',
      location: profile.location ?? '',
      university_id: profile.university?.id ?? '',
      degree: profile.degree ?? '',
      field_of_study: profile.field_of_study ?? '',
      graduation_year: profile.graduation_year ?? undefined,
      preferred_work_mode: profile.preferred_work_mode ?? undefined,
      preferred_location: profile.preferred_location ?? '',
      date_of_birth: profile.date_of_birth ?? '',
      available_from: profile.available_from ?? '',
      expected_stipend_min: profile.expected_stipend_min ?? '',
      portfolio_url: profile.portfolio_url ?? '',
      linkedin_url: profile.linkedin_url ?? '',
      github_url: profile.github_url ?? '',
      open_to_opportunities: profile.open_to_opportunities,
    });
  }, [profile, reset]);

  const save = useMutation({
    mutationFn: (values) => studentApi.updateProfile(values),
    onSuccess: (updated) => {
      toast.success('Profile updated');
      queryClient.setQueryData(['student-profile'], updated);
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['profile-completion'] });
    },
  });

  // The section only exists once the profile has loaded, too late for the
  // browser's own jump to #account -- so make it here.
  // useEffect(() => {
  //   if (profile && hash === '#account') {
  //     document.getElementById('account')?.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [profile, hash]);
  // useEffect(() => {
  //   if (profile && ['#account', '#education', '#experience', '#skills'].includes(hash)) {
  //     document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [profile, hash]);
  // A link to `#account` (the account menu's "Change password") opens that tab.
  useEffect(() => {
    const next = tabFromHash(hash);
    if (next) setTab(next);
  }, [hash]);

  function chooseTab(key) {
    setTab(key);
    navigate({ hash: key === 'profile' ? '' : key }, { replace: true });
  }

  if (isPending) return <LoadingState label="Loading your profile…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  async function onSubmit(values) {
    // Empty strings mean "clear this field" -- send null, not "".
    const payload = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, value === '' ? null : value]),
    );
    try {
      await save.mutateAsync(payload);
      onSaved?.();
    } catch (mutationError) {
      if (!applyFieldErrors(mutationError, setError)) {
        toast.error(errorMessage(mutationError, 'Could not save the profile'));
      }
    }
  }

  return (
    <div>
      <SegmentedTabs
        label="Profile sections"
        tabs={PROFILE_TABS}
        value={tab}
        onChange={chooseTab}
        variant="pills"
        className="mb-6"
      />
      <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        {/* The projects list sits beside the form rather than inside it: it
            saves each entry on its own, and a form cannot nest another. */}
        <div className="space-y-6">
          {/* The video first on the Profile tab too, so it is the first thing a
              student sees there. It saves on its own, outside the form. */}
          <div hidden={tab !== 'profile'}>
            <ProfileVideoField videoUrl={profile.intro_video_url} />
          </div>

          {/* Hidden rather than unmounted, so switching tab keeps what was typed. */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
            noValidate
            hidden={tab !== 'profile'}
          >
            <Card>
              <CardHeader
                title="About you"
                description="The first lines a company reads about you."
              />
              <div className="space-y-4">
                <ProfilePhotoField
                  imageUrl={profile.profile_image_url}
                  fullName={profile.full_name}
                />
                {/* One line that grows instead of a single-line input, so a long
                  headline is never cut off; Enter is not a line break here. */}
                <Textarea
                  label="Headline"
                  rows={1}
                  placeholder="Computer Science student at TU Delft"
                  className="resize-none [field-sizing:content]"
                  onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
                  hint={<CharacterCount value={headline} limits={HEADLINE_LIMITS} />}
                  error={errors.headline?.message}
                  {...register('headline')}
                />
                <Textarea
                  label="Bio"
                  placeholder="What are you studying, what do you enjoy building?"
                  hint={<CharacterCount value={bio} limits={BIO_LIMITS} />}
                  error={errors.bio?.message}
                  {...register('bio')}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Location"
                    placeholder="Kolkata, India"
                    error={errors.location?.message}
                    {...register('location')}
                  />
                  <Select
                    label="Graduation year"
                    error={errors.graduation_year?.message}
                    {...register('graduation_year')}
                  >
                    <option value="">Not set</option>
                    {/* A year saved before the range narrowed still shows, so
                      the student can see why Save asks for a change. */}
                    {profile.graduation_year &&
                      !graduationYears().includes(profile.graduation_year) && (
                        <option value={profile.graduation_year}>{profile.graduation_year}</option>
                      )}
                    {graduationYears().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                  {/* Companies never see this; it is on the profile because
                      some placements have an age requirement and the student
                      should be the one who states it. */}
                  <Input
                    label="Date of birth"
                    type="date"
                    min={earliestBirthDate()}
                    max={latestBirthDate()}
                    hint={`Born in ${latestBirthYear()} or earlier.`}
                    error={errors.date_of_birth?.message}
                    {...register('date_of_birth')}
                  />
                </div>
              </div>
            </Card>

            {/* Moved to the Education tab, with its own Save button.
            <Card>
              <CardHeader title="Studies" description="Where and what you are studying." />
              <div className="grid gap-4 sm:grid-cols-2">
                <Select label="University" {...register('university_id')}>
                  <option value="">Not selected</option>
                  {universities?.map((university) => (
                    <option key={university.id} value={university.id}>
                      {university.name}
                    </option>
                  ))}
                </Select>
                <Input label="Degree" error={errors.degree?.message} {...register('degree')} />
                <Input
                  label="Field of study"
                  error={errors.field_of_study?.message}
                  {...register('field_of_study')}
                />
              </div>
            </Card>
            */}

            <Card>
              <CardHeader
                title="Preferences"
                description="Used to match you with the right internships."
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Select label="Preferred work mode" {...register('preferred_work_mode')}>
                  <option value="">No preference</option>
                  <option value="ONSITE">Onsite</option>
                  <option value="REMOTE">Remote</option>
                  <option value="HYBRID">Hybrid</option>
                </Select>
                <Input
                  label="Preferred location"
                  error={errors.preferred_location?.message}
                  {...register('preferred_location')}
                />
                <Input
                  label="Available from"
                  type="date"
                  max={latestAvailableFrom()}
                  error={errors.available_from?.message}
                  {...register('available_from')}
                />
                <Input
                  label="Expected stipend (minimum)"
                  inputMode="decimal"
                  placeholder="Per month, in EUR"
                  hint="Optional. Leave blank if you have no minimum."
                  error={errors.expected_stipend_min?.message}
                  {...register('expected_stipend_min')}
                />
              </div>
              <label className="border-line bg-surface-muted/40 rounded-input mt-4 flex items-center gap-2 border px-3 py-2.5 text-sm">
                <input
                  type="checkbox"
                  className="border-line text-brand-600 size-4 rounded border"
                  {...register('open_to_opportunities')}
                />
                <span className="text-ink-muted">
                  Open to opportunities (companies can find my profile)
                </span>
              </label>
            </Card>

            <Card>
              <CardHeader title="Links" description="Where companies can see more of your work." />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Portfolio"
                  wrapperClassName="sm:col-span-2"
                  placeholder="https://"
                  error={errors.portfolio_url?.message}
                  {...register('portfolio_url')}
                />
                <Input
                  label="LinkedIn"
                  placeholder="https://linkedin.com/in/…"
                  error={errors.linkedin_url?.message}
                  {...register('linkedin_url')}
                />
                <Input
                  label="GitHub"
                  placeholder="https://github.com/…"
                  error={errors.github_url?.message}
                  {...register('github_url')}
                />
              </div>
            </Card>

            <div className="flex justify-end gap-3">
              <Button type="submit" isLoading={isSubmitting} disabled={!isDirty}>
                <Icon name="check" size={16} />
                Save changes
              </Button>
            </div>
          </form>

          <div hidden={tab !== 'profile'}>
            <PortfolioProjectsCard />
          </div>

          {/* What were their own menu entries, now sections of this page. */}
          <section id="profile-education" className="scroll-mt-24" hidden={tab !== 'education'}>
            {/* Studies, off for now at the client's request. The same form as the
                Profile tab: these fields save with the rest of the profile.
            <form onSubmit={handleSubmit(onSubmit)} className="mb-8 space-y-3" noValidate>
              <Card>
                <CardHeader title="Studies" description="Where and what you are studying." />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select label="University" {...register('university_id')}>
                    <option value="">Not selected</option>
                    {universities?.map((university) => (
                      <option key={university.id} value={university.id}>
                        {university.name}
                      </option>
                    ))}
                  </Select>
                  <Input label="Degree" error={errors.degree?.message} {...register('degree')} />
                  <Input
                    label="Field of study"
                    error={errors.field_of_study?.message}
                    {...register('field_of_study')}
                  />
                </div>
              </Card>
              <div className="flex justify-end">
                <Button type="submit" isLoading={isSubmitting} disabled={!isDirty}>
                  <Icon name="check" size={16} />
                  Save studies
                </Button>
              </div>
            </form>
            */}
            <StudentEducationPage embedded />
          </section>
          <section id="profile-experience" className="scroll-mt-24" hidden={tab !== 'experience'}>
            <StudentExperiencePage embedded />
          </section>
          <section id="profile-skills" className="scroll-mt-24" hidden={tab !== 'skills'}>
            <StudentSkillsPage embedded />
          </section>

          <section id="profile-video" className="scroll-mt-24" hidden={tab !== 'video'}>
            <ProfileVideoField videoUrl={profile.intro_video_url} />
          </section>

          <section id="profile-billing" className="scroll-mt-24" hidden={tab !== 'billing'}>
            {/* Only while open: it loads billing, and needs no typing kept. */}
            {tab === 'billing' && <StudentPlanPage embedded />}
          </section>

          <section
            id="profile-account"
            className="scroll-mt-24 space-y-6"
            hidden={tab !== 'account'}
          >
            <h2 className="text-ink pt-2 text-lg font-semibold">Account</h2>
            <AccountSettings />
          </section>
        </div>

        <aside className="space-y-6">
          <ProfileCvCard />
          <PortfolioShareCard />

          {/* Taken off the profile at the client's request.
        <ProfileCompletionCard percentage={profile.profile_completion} />

        <CareerGoalCard goal={profile.career_goal} category={profile.target_job_category} />

        <UniversityEmailCard
          currentEmail={profile.university_email}
          verified={profile.university_email_verified}
        />
        */}
        </aside>
      </div>
    </div>
  );
}

/**
 * "12 / 150 characters · at least 10" under a text field, so the limits are
 * visible while typing rather than only after Save. Turns red when out of range.
 */
function CharacterCount({ value, limits }) {
  const length = value.trim().length;
  const outOfRange = length > limits.max || (length > 0 && length < limits.min);
  return (
    <span className={outOfRange ? 'text-danger-600' : undefined}>
      {length.toLocaleString()} / {limits.max.toLocaleString()} characters · at least {limits.min}
    </span>
  );
}

/**
 * Where the student is heading, shown but not edited here.
 *
 * Read-only on purpose. The goal and the field are set together on the career
 * page, where the sentence is read into a field and the field is backed by a
 * path counted from live listings. A second editable copy on this form would
 * let the two drift, and picking a category from a dropdown with none of that
 * context is the part of the flow worth keeping.
 */
// Not shown now (see the profile's side column); kept to bring back.
// eslint-disable-next-line no-unused-vars
function CareerGoalCard({ goal, category }) {
  return (
    <Card>
      <CardHeader
        title="Where you are heading"
        action={<Icon name="target" size={18} className="text-brand-600" />}
      />
      {category ? (
        <div className="space-y-3">
          <Badge tone="brand">{category.name}</Badge>
          {goal && <p className="text-ink-muted text-sm italic">“{goal}”</p>}
          <LinkButton to={paths.student.career} variant="secondary" size="sm">
            View my path
            <Icon name="arrowRight" size={15} />
          </LinkButton>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-ink-muted text-sm">
            Not set. Say what you want to do and we will show you what open internships in that
            field are asking for — useful before you have anything to list.
          </p>
          <LinkButton to={paths.student.career} variant="secondary" size="sm">
            Plan my path
            <Icon name="arrowRight" size={15} />
          </LinkButton>
        </div>
      )}
    </Card>
  );
}

// Not shown now (see the profile's side column); kept to bring back.
// eslint-disable-next-line no-unused-vars
function UniversityEmailCard({ currentEmail, verified }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState(currentEmail ?? '');
  const [code, setCode] = useState('');
  const [debugCode, setDebugCode] = useState(null);
  const [codeSent, setCodeSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [codeError, setCodeError] = useState(null);

  function requestCode() {
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setEmailError(result.error.issues[0].message);
      return;
    }
    setEmailError(null);
    sendCode.mutate();
  }

  function submitCode() {
    if (!/^\d{6}$/.test(code.trim())) {
      setCodeError('Enter the 6-digit code from the email');
      return;
    }
    setCodeError(null);
    verify.mutate();
  }

  const sendCode = useMutation({
    mutationFn: () => studentApi.requestUniversityEmailCode({ university_email: email.trim() }),
    onSuccess: (result) => {
      setCodeSent(true);
      setDebugCode(result.debug_code ?? null);
      toast.success('Verification code sent');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not send the code')),
  });

  const verify = useMutation({
    mutationFn: () => studentApi.verifyUniversityEmail(code.trim()),
    onSuccess: (profile) => {
      toast.success('University email verified');
      queryClient.setQueryData(['student-profile'], profile);
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
      setCodeSent(false);
      setCode('');
    },
    onError: (error) => toast.error(errorMessage(error, 'That code did not work')),
  });

  return (
    <Card>
      <CardHeader title="University email" />
      {verified ? (
        <div className="space-y-2">
          <Badge tone="success" dot>
            Verified
          </Badge>
          <p className="text-ink-muted text-sm">{currentEmail}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-ink-muted text-sm">
            Verifying your university address adds a trust badge to your profile.
          </p>
          <Input
            label="University email"
            type="email"
            value={email}
            error={emailError}
            onChange={(event) => {
              setEmail(event.target.value);
              setEmailError(null);
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            isLoading={sendCode.isPending}
            disabled={!email.trim()}
            onClick={requestCode}
          >
            Send code
          </Button>

          {debugCode && (
            <Alert tone="info">
              Development code: <span className="font-mono font-semibold">{debugCode}</span>
            </Alert>
          )}

          {codeSent && (
            <>
              <Input
                label="Code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                error={codeError}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, ''));
                  setCodeError(null);
                }}
              />
              <Button size="sm" fullWidth isLoading={verify.isPending} onClick={submitCode}>
                Verify
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
